import type { ReactNode } from 'react';
import { Info } from 'lucide-react';

import { cn } from '@/lib/utils';
import { SimpleTooltip } from '@/components/SimpleTooltip';

/**
 * The small-caps row label — the app's single most-repeated hand-built recipe.
 *
 * `khorium/no-primitive-recipe` reports 145 sites spelling this shape by hand
 * (uppercase + a tracking step + `text-muted-foreground` at `text-3xs`/`text-2xs`),
 * more than the Badge, Alert and panel-shell recipes put together. ENG-1742
 * promotes it out of `components/ux-redesign/fields.tsx`, where it had been
 * living as part of a Storybook-only proposal, so those sites have a real
 * shared component to land on.
 *
 * ## Why a `<span>` by default
 *
 * Most of the reported sites are not labelling a form control — they caption a
 * read-only value, head a group of rows, or title a sub-section. Rendering
 * those as `<label>` would invent an association that does not exist: a
 * `<label>` with neither `htmlFor` nor a wrapped control is announced as a
 * label for nothing, and it makes the *real* unlabelled-field problem
 * (ENG-1460) harder to find, not easier. So the default element is a `<span>`,
 * and `htmlFor` is the explicit opt-in that turns it into a real `<label>` —
 * you pass it exactly when there is a control with that id to point at.
 *
 * ## Why `tracking-wider`
 *
 * Measured across `src/`, class strings that pair `uppercase` with a tracking
 * step: `tracking-wider` 130, `tracking-caps` 47, `tracking-wide` 26,
 * `tracking-caps-wide` 8, `tracking-widest` 2. Within the sites the lint rule
 * actually reports it is 99 to 31. `tracking-wider` is the majority spelling by
 * roughly 3:1, so standardising on it is the migration that moves the fewest
 * pixels. (ENG-1742's description has this ratio the other way round; the
 * numbers above are the measurement, not the ticket.)
 *
 * ## No layout of its own
 *
 * Deliberately carries no margin and no width. The prototype version hardcoded
 * `mb-1 w-fit`; a shared component that ships a 4px bottom margin makes every
 * adopter that does not want one write `mb-0`, which is how a primitive
 * acquires an override at most of its call sites. Spacing is the parent's job.
 * `inline-flex` (rather than `flex w-fit`) keeps the element content-sized
 * without spending a width utility an adopter would have to fight to build a
 * full-width `justify-between` row.
 *
 * ## Why the rule does not fire on this file
 *
 * `khorium/no-primitive-recipe` row 4 names `RowLabel`, and the rule skips a
 * recipe in any file that DECLARES AND EXPORTS the primitive that recipe points
 * at — this file, necessarily, since it is the shared implementation. That
 * guard keys on the primitive's identity rather than on a path, so it survives
 * this component moving directory, which is why stage one's
 * `eslint-disable-next-line` and a `files:` carve-out in `eslint.config.ts`
 * were both retired in favour of it. It also skips the RECIPE, not the FILE:
 * if this component ever grew a hand-built pill or banner, the rule would
 * still say so. `eslint-rules/no-primitive-recipe.test.ts` feeds this file's
 * real source to the rule so the exemption cannot go quiet unnoticed.
 */

/**
 * The `size` axis. Type scale only, and only the one size-bearing class that
 * differs between rungs — the same shape as `badge.tsx`'s local `size` axis,
 * for the same reason: everything a rung does NOT change stays in the shared
 * base string, so `default` renders byte-for-byte what this component rendered
 * before the axis existed.
 *
 * Two rungs because the migration found two: of the 144 sites
 * `khorium/no-primitive-recipe` reports, 130 are `text-3xs` and 14 are
 * `text-2xs`. A third rung would be inventing a size nothing asks for.
 *
 * A plain record rather than a `cva`, per `frontend/.agents/skills/khorium-frontend-ui`:
 * the axis drives exactly one class on one element, and a `cva` config here
 * would be more code than it replaces. (It would also buy nothing from
 * `variant-story-parity.test.ts`, whose glob is `src/components/ui/*.tsx` and
 * does not reach this file — the stories below are hand-written on purpose,
 * not gate-driven.)
 */
const SIZE_CLASS = {
  default: 'text-3xs',
  '2xs': 'text-2xs',
} as const;

export type RowLabelSize = keyof typeof SIZE_CLASS;

export function RowLabel({
  children,
  tooltip,
  htmlFor,
  size = 'default',
  id,
  className,
}: {
  children: ReactNode;
  /** Explanation shown behind a visible ⓘ next to the label. */
  tooltip?: string;
  /**
   * Type scale. `default` is `text-3xs`, the size 130 of the 144 migrated
   * sites used; `2xs` is the `text-2xs` rung the other 14 used. Named for the
   * rung rather than `sm`/`lg`, so the prop cannot disagree with the class.
   */
  size?: RowLabelSize;
  /**
   * Id of the control this labels. Supplying it renders a real `<label
   * htmlFor>`; omitting it renders a `<span>`. Pass it only when a control
   * with that id exists — see the note above.
   */
  htmlFor?: string;
  /**
   * The other half of the labelling pattern. A `<button>`-backed control (a
   * `<Select>` trigger, say) IS labelable, so `htmlFor` would name it and also
   * pop it open on a click of its own label; the association is made with
   * `aria-labelledby` pointing back at this id instead. ENG-1742 found three
   * live sites doing exactly that; the reasoning is written out above
   * `ChtOverrideLabel`'s call site in `ChtSolverSections.tsx`.
   */
  id?: string;
  className?: string;
}) {
  const classes = cn(
    'inline-flex items-center gap-1 tracking-wider text-muted-foreground uppercase',
    SIZE_CLASS[size],
    className,
  );

  // `size-3`, not `h-3 w-3`: tailwind-merge groups the two separately, so the
  // pair survives an adopter's `size-*` override and the icon silently keeps
  // its old box. See `frontend/.agents/skills/khorium-frontend-ui`.
  const content = (
    <>
      {children}
      {tooltip && <Info className="size-3 shrink-0" aria-hidden />}
    </>
  );

  const label = htmlFor ? (
    <label htmlFor={htmlFor} id={id} className={classes}>
      {content}
    </label>
  ) : (
    <span id={id} className={classes}>
      {content}
    </span>
  );

  if (!tooltip) return label;
  return (
    <SimpleTooltip content={tooltip} side="right">
      {label}
    </SimpleTooltip>
  );
}
